import { type ColumnDef } from '@tanstack/react-table';
import { Pencil } from 'lucide-react';
import { Button } from '@/components/ui/shadcn';
import { cn } from '@/utils';
import btcIcon from '../../../assets/coins/btc.svg';
import ltcIcon from '../../../assets/coins/ltc.svg';
import dogeIcon from '../../../assets/coins/doge.svg';
import ethIcon from '../../../assets/coins/eth.svg';
import usdtIcon from '../../../assets/coins/usdt.svg';
import pyusdIcon from '../../../assets/coins/pyusd.svg';
import paypalIcon from '../../../assets/coins/paypal.svg';
import cashappIcon from '../../../assets/coins/cashapp.svg';
import cardIcon from '../../../assets/coins/card.svg';
import type { EditableRow, SupportedByKeyMap } from './providerFeesTypes';
import {
  formatOnlineDebtMethodLabel,
  formatZeroxMethodIdLabel,
  normalizeSupportedMethodId,
  prettyMethodLabel,
  splitCoinAndNetworkLabel,
  toPercentString,
  toRateNumber,
  toSupportedKey,
  formatNetworkFeeDisplay,
} from './providerFeesUtils';

function getCoinVisual(methodId: string, methodLabel: string): { iconSrc?: string; symbol: string; colorClass: string; textClass: string } {
  const id = methodId.toLowerCase();
  const label = methodLabel.toLowerCase();
  const isBtc = id.includes('btc') || id.includes('bitcoin') || label.includes('bitcoin');
  const isLtc = id.includes('ltc') || id.includes('litecoin') || label.includes('litecoin');
  const isDoge = id.includes('doge') || id.includes('dogecoin') || label.includes('dogecoin');
  const isEth = id.includes('eth') || id.includes('erc') || label.includes('ethereum');
  const isUsdt = id.includes('usdt') || id.includes('trc') || label.includes('tether');
  const isPyusd = id.includes('pyusd') || label.includes('paypal usd');
  const isPaypal = id.includes('paypal') || label.includes('paypal');
  const isCashApp = id.includes('cash_app') || id.includes('ecash') || label.includes('cash app');
  const isCard = id.includes('card') || id.includes('credit') || label.includes('card');
  if (isBtc) return { iconSrc: btcIcon, symbol: '₿', colorClass: 'bg-amber-500/20', textClass: 'text-amber-600 dark:text-amber-300' };
  if (isLtc) return { iconSrc: ltcIcon, symbol: 'Ł', colorClass: 'bg-slate-500/20', textClass: 'text-slate-700 dark:text-slate-300' };
  if (isDoge) return { iconSrc: dogeIcon, symbol: 'Ð', colorClass: 'bg-yellow-500/20', textClass: 'text-yellow-700 dark:text-yellow-300' };
  if (isEth) return { iconSrc: ethIcon, symbol: 'Ξ', colorClass: 'bg-indigo-500/20', textClass: 'text-indigo-700 dark:text-indigo-300' };
  if (isUsdt) return { iconSrc: usdtIcon, symbol: 'T', colorClass: 'bg-emerald-500/20', textClass: 'text-emerald-700 dark:text-emerald-300' };
  if (isPyusd) return { iconSrc: pyusdIcon, symbol: 'P', colorClass: 'bg-cyan-500/20', textClass: 'text-cyan-700 dark:text-cyan-300' };
  if (isPaypal) return { iconSrc: paypalIcon, symbol: 'P', colorClass: 'bg-blue-500/20', textClass: 'text-blue-700 dark:text-blue-300' };
  if (isCashApp) return { iconSrc: cashappIcon, symbol: '$', colorClass: 'bg-green-500/20', textClass: 'text-green-700 dark:text-green-300' };
  if (isCard) return { iconSrc: cardIcon, symbol: 'C', colorClass: 'bg-purple-500/20', textClass: 'text-purple-700 dark:text-purple-300' };
  return { symbol: '•', colorClass: 'bg-zinc-500/20', textClass: 'text-zinc-700 dark:text-zinc-300' };
}

export function providerFeeRowId(row: EditableRow): string {
  return `${(row.provider || '').toLowerCase()}::${row.operation}::${(row.methodId || 'all').toLowerCase()}`;
}

/** Combined total like the mockup: base percent plus a network-fee suffix ("+ $5" / "+ network"). */
function formatTotalFeeDisplay(row: EditableRow): string {
  const basePct = toRateNumber(row.paymentGateFeePercent) + toRateNumber(row.systemFeePercent);
  const baseLabel = `${toPercentString(basePct)}%`;
  if (row.networkFeeType === 'fixed' && Number(row.networkFeeFixedUsd) > 0) {
    return `${baseLabel} + $${row.networkFeeFixedUsd}`;
  }
  if (row.networkFeeType === 'percent' && Number(row.networkFeePercent) > 0) {
    return `${baseLabel} + network`;
  }
  return baseLabel;
}

export function getProviderFeeColumns(ctx: {
  supportedByKey: SupportedByKeyMap;
  onEdit: (row: EditableRow) => void;
  onToggle: (row: EditableRow) => void;
  disabled?: boolean;
}): ColumnDef<EditableRow>[] {
  const { supportedByKey, onEdit, onToggle, disabled = false } = ctx;
  const isStaticWalletFeeRow = (row: EditableRow) =>
    (row.provider || '').toLowerCase() === 'zeroxprocessing' &&
    (row.methodId || '').toLowerCase() === 'zeroxprocessing_static_wallet';

  return [
    {
      id: 'method',
      header: () => <span className="whitespace-nowrap">Currency / Network</span>,
      cell: ({ row }) => {
        const r = row.original;
        const provider = (r.provider || '').toLowerCase();
        const operation = r.operation;
        const methodId = (r.methodId || 'all').toLowerCase();
        if (operation === 'transfer') {
          return '—';
        }
        const key = toSupportedKey(provider, String(operation || ''));
        const supported = supportedByKey[key] || [];
        if (provider === 'online_debt') {
          const methodLabel = formatOnlineDebtMethodLabel(methodId);
          return (
            <div className="flex min-w-0 items-start gap-2">
              <span className="inline-flex h-6 w-6 shrink-0 items-center justify-center rounded-full bg-sky-500/20 text-xs font-bold text-sky-700 dark:text-sky-300">
                $
              </span>
              <span className="min-w-0">
                <span className="block truncate text-sm font-semibold">{methodLabel}</span>
                <span className="block truncate text-xs text-muted-foreground">USD wallet movement</span>
              </span>
            </div>
          );
        }

        const matched = supported.find(
          (m) => normalizeSupportedMethodId(m).toLowerCase() === methodId
        );
        const methodLabel =
          methodId === 'all'
            ? 'All'
            : matched
              ? prettyMethodLabel(matched)
              : methodId.startsWith('zerox_')
                ? formatZeroxMethodIdLabel(methodId)
                : methodId;
        const { coin: coinPart, network: networkPart } = splitCoinAndNetworkLabel(methodLabel, methodId);
        const coinVisual = getCoinVisual(methodId, methodLabel);
        return (
          <div className="flex min-w-0 items-start gap-2">
            {coinVisual.iconSrc ? (
              <img src={coinVisual.iconSrc} alt={coinPart} className="h-6 w-6 shrink-0 rounded-full" />
            ) : (
              <span
                className={cn('inline-flex h-6 w-6 shrink-0 items-center justify-center rounded-full text-xs font-bold', coinVisual.colorClass, coinVisual.textClass)}
              >
                {coinVisual.symbol}
              </span>
            )}
            <span className="min-w-0">
              <span className="block truncate text-sm font-semibold">{coinPart}</span>
              {networkPart ? (
                <span className="block truncate text-xs text-muted-foreground">{networkPart}</span>
              ) : null}
              {methodId.startsWith('zerox_') ? (
                <span className="block truncate font-mono text-[10px] text-muted-foreground/80">{methodId}</span>
              ) : null}
            </span>
          </div>
        );
      },
    },
    {
      accessorKey: 'paymentGateFeePercent',
      header: () => <div className="text-right whitespace-nowrap">Gateway processing fee</div>,
      cell: ({ row }) => (
        <button
          type="button"
          className="w-full text-right tabular-nums underline-offset-2 hover:underline disabled:cursor-not-allowed disabled:no-underline disabled:opacity-60"
          onClick={() => onEdit(row.original)}
          disabled={disabled}
        >
          {String(row.original.paymentGateFeePercent || '0')}%
        </button>
      ),
    },
    {
      id: 'networkFee',
      header: () => <div className="text-right whitespace-nowrap">Network fee</div>,
      cell: ({ row }) => {
        const r = row.original;
        const isLive = r.networkFeeSource === 'zerox_live';
        return (
          <button
            type="button"
            className="w-full text-right tabular-nums underline-offset-2 hover:underline disabled:cursor-not-allowed disabled:no-underline disabled:opacity-60"
            onClick={() => onEdit(row.original)}
            disabled={disabled}
            title={isLive ? 'Live network fee from 0xprocessing (read-only)' : undefined}
          >
            <span className="inline-flex items-center justify-end gap-1.5">
              {isLive ? (
                <span className="rounded-sm bg-emerald-500/15 px-1 py-0.5 text-[10px] font-semibold uppercase tracking-wide text-emerald-700 dark:text-emerald-300">
                  live
                </span>
              ) : null}
              {formatNetworkFeeDisplay(r.networkFeeType, r.networkFeePercent, r.networkFeeFixedUsd)}
            </span>
          </button>
        );
      },
    },
    {
      accessorKey: 'systemFeePercent',
      header: () => <div className="text-right whitespace-nowrap">System fee</div>,
      cell: ({ row }) => (
        <button
          type="button"
          className="w-full text-right tabular-nums underline-offset-2 hover:underline disabled:cursor-not-allowed disabled:no-underline disabled:opacity-60"
          onClick={() => onEdit(row.original)}
          disabled={disabled}
        >
          {String(row.original.systemFeePercent || '0')}%
        </button>
      ),
    },
    {
      id: 'feeTotal',
      header: () => <div className="text-right whitespace-nowrap">Total Fee</div>,
      cell: ({ row }) => (
        <button
          type="button"
          className="w-full text-right font-medium tabular-nums underline-offset-2 hover:underline disabled:cursor-not-allowed disabled:no-underline disabled:opacity-60"
          onClick={() => onEdit(row.original)}
          disabled={disabled}
        >
          {formatTotalFeeDisplay(row.original)}
        </button>
      ),
    },
    {
      id: 'activity',
      header: () => <div className="text-center whitespace-nowrap">Status</div>,
      cell: ({ row }) => {
        if (isStaticWalletFeeRow(row.original)) {
          return <div className="text-center text-muted-foreground">—</div>;
        }
        return (
          <div className="text-center">
            <button
              type="button"
              onClick={() => onToggle(row.original)}
              disabled={disabled}
              className={`h-6 w-11 rounded-full transition disabled:cursor-not-allowed disabled:opacity-50 ${
                row.original.isEnabled !== false ? 'bg-emerald-500' : 'bg-zinc-400'
              }`}
              aria-label={row.original.isEnabled !== false ? 'Disable method' : 'Enable method'}
            >
              <span
                className={`block h-5 w-5 rounded-full bg-white transition ${
                  row.original.isEnabled !== false ? 'translate-x-5' : 'translate-x-0'
                }`}
              />
            </button>
          </div>
        );
      },
    },
    {
      id: 'actions',
      header: () => <div className="text-right whitespace-nowrap">Action</div>,
      cell: ({ row }) => (
        <div className="flex justify-end gap-1">
          <Button
            type="button"
            variant="ghost"
            size="icon"
            className="h-8 w-8"
            onClick={() => onEdit(row.original)}
            disabled={disabled}
            aria-label="Edit"
          >
            <Pencil className="h-4 w-4" />
          </Button>
        </div>
      ),
    },
  ];
}
